import { useQuery } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

export interface SectionTypeTooltip {
  id: string;
  tooltipImageUrl: string | null;
  tooltipText: string;
}

export interface SectionType {
  id: string;
  displayName: string;
  version: number;
  supportsQuickCreate: boolean;
  iconUrl?: string | null;
  tooltip?: SectionTypeTooltip | null;
}

export interface SectionTypeCategory {
  id: string;
  displayName: string;
  position: number;
}

export interface SectionTypeGroup {
  // Section types that have not been categorised yet arrive in a group with no category.
  category: SectionTypeCategory | null;
  sectionTypes: SectionType[];
}

interface GroupedSectionTypesResponse {
  groups: SectionTypeGroup[];
}

async function fetchGroupedSectionTypes(organisationId: string) {
  const { data } = await axiosPrivate.get<GroupedSectionTypesResponse>(
    '/v3/pharmacist/categorized-page-section-types',
    { params: { organisationId } }
  );
  return data;
}

export function useGetGroupedSectionTypes(organisationId: string) {
  // A disabled query stays in `status: 'loading'` in react-query v4, so `isLoading` alone would
  // leave the picker spinning forever without an organisationId. `isInitialLoading` additionally
  // requires an in-flight fetch.
  const { data, isInitialLoading, isError, error } = useQuery({
    queryKey: ['categorized-page-section-types', organisationId],
    queryFn: () => fetchGroupedSectionTypes(organisationId),
    enabled: Boolean(organisationId),
    meta: { errorMessage: 'Failed to load section types' },
    // The API owns the ordering intent through position, so it is honoured here rather than
    // relying on the order the groups happen to arrive in. Uncategorised groups sort last.
    select: (response) =>
      [...response.groups].sort(
        (a, b) =>
          (a.category?.position ?? Number.MAX_SAFE_INTEGER) -
          (b.category?.position ?? Number.MAX_SAFE_INTEGER)
      ),
  });

  return {
    groups: data ?? [],
    isLoading: isInitialLoading,
    isError,
    error,
  };
}
